import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import useAuth from '../auth/useAuth';
import NotFound from '../pages/NotFound';
import PageLayout from './PageLayout';
import ArticleView from './ArticleView';
import ArticleBody from './ArticleBody';
import ArticleSidebar from './ArticleSidebar';
import PageMain from './ui/PageMain';

import {
  getArticle,
  getAuthoringDepartments,
  updateArticle,
  archiveArticle,
} from '../api';

import useArticleDraft from './useArticleDraft';
import ArticleEditorForm, { ARTICLE_TITLE_ID } from './ArticleEditorForm';
import { EXTERNAL_URL } from './ArticleLinkField';
import usePlugins from '../plugins/usePlugins';
import { pluginArticle } from '../plugins/pluginBlocks';
import ExternalLinkNotice from './ExternalLinkNotice';
import {
  isFavorite as checkFavorite,
  addFavorite,
  removeFavorite,
} from '../favorites';

import { extractHeadings } from '../utils/articleHeadings';
import { missingTitleMessage, titleErrorMessage } from '../utils/articleTitle';
import useActiveHeading from './useActiveHeading';

function ArticlePage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { id } = useParams();

  // Ein News-Beitrag wird über ?edit=1 direkt im Bearbeitungsmodus geöffnet.
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [article, setArticle] =
    useState(null);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState('');

  const [isEditing, setIsEditing] =
    useState(false);

  const draft = useArticleDraft();
  const plugins = usePlugins();
  const [editDepartmentId, setEditDepartmentId] = useState(null);

  const [authoringDepartments, setAuthoringDepartments] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  // Für den erneuten Import werden dieselben Rechte wie beim Erstellen benötigt.
  const canReimport =
    user?.rightsAnywhere?.includes('articles.manage') ?? false;

  const [isFavorite, setIsFavorite] = useState(false);

  // Den Artikel archivieren und anschließend zur Artikelliste zurückkehren.
  const onArchive = async () => {
    if (!article || !article.canEdit) return;

    // Die Archivierung vom Benutzer bestätigen lassen.
    const confirmed = window.confirm(
      'Möchten Sie diesen Artikel wirklich archivieren?'
    );

    if (!confirmed) return;

    try {
      // Den Artikel archivieren und zur Artikelliste zurückkehren.
      await archiveArticle(article.article_id);
      navigate('/articles', { replace: true });
    } catch (err) {
      // Den Fehler protokollieren und den Benutzer informieren.
      console.error('Fehler beim Archivieren des Artikels:', err);
      window.alert('Der Artikel konnte nicht archiviert werden.');
    }
  };

  useEffect(() => {
    let cancelled = false;

    if (!article) {
      return undefined;
    }

    // Die angebotenen Abteilungen hängen davon ab, ob es ein News-Beitrag ist.
    getAuthoringDepartments(article.is_news)
      .then((list) => {
        if (!cancelled) {
          setAuthoringDepartments(list ?? []);
        }
      })
      .catch((loadError) => {
        // Ohne Abteilungsliste kann das Feld weiterhin als Text angezeigt werden.
        console.error('Failed to load authoring departments:', loadError);
      });

    return () => {
      cancelled = true;
    };
  }, [article?.is_news]); // eslint-disable-line react-hooks/exhaustive-deps

  // Den angeforderten Artikel laden.
  useEffect(() => {
    let cancelled = false;

    async function loadArticle() {
      try {
        setLoading(true);
        setError('');
        setArticle(null);
        setIsEditing(false);

        const data = await getArticle(id);

        if (!cancelled) {
          setArticle(data);
        }
      } catch (err) {
        console.error('Failed to load article:', err);

        if (!cancelled) {
          setError('Article does not exist');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadArticle();

    return () => {
      cancelled = true;
    };
  }, [id]);

  // ?edit=1 öffnet den Editor und wird anschließend aus der URL entfernt.
  useEffect(() => {
    if (!article?.canEdit || searchParams.get('edit') !== '1') {
      return;
    }

    startEditing();
    setSearchParams({}, { replace: true });

    // startEditing verwendet den gerade geladenen Artikel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [article, searchParams]);

  // News-Beiträge werden außerhalb des Bearbeitungsmodus auf ihrer News-Seite angezeigt.
  useEffect(() => {
    if (
      article?.is_news &&
      !isEditing &&
      (searchParams.get('edit') !== '1' || !article.canEdit)
    ) {
      navigate(`/news/${article.article_id}`, { replace: true });
    }
  }, [article, isEditing, searchParams, navigate]);

  const articleText = article?.text ?? '';

  const headings = useMemo(
    () => extractHeadings(articleText),
    [articleText]
  );

  const activeHeadingId = useActiveHeading(headings);

  useEffect(() => {
    async function loadFavorite() {
      try {
        const favorite = await checkFavorite(id);
        setIsFavorite(favorite.isFavorite);
      } catch (err) {
        console.error('Failed to check favorite status:', err);
      }
    }

    if (id) {
      loadFavorite();
    }
  }, [id]);


  function startEditing() {
    draft.load({
      title: article.title ?? '',
      text: article.text ?? '',
      tags: Array.isArray(article.tags) ? article.tags : [],
      icon: article.icon ?? null,
      summary: article.summary ?? '',
      externalUrl: article.external_url ?? '',
    });
    setEditDepartmentId(article.department?.department_id ?? null);
    setIsEditing(true);
  }

  function cancelEditing() {
    draft.discardPending();
    setIsEditing(false);
    window.scrollTo(0, 0);
  }

  async function toggleFavorite() {
    try {
      if (isFavorite) {
        await removeFavorite(id);
        setIsFavorite(false);
      } else {
        await addFavorite(id);
        setIsFavorite(true);
      }
    } catch (err) {
      console.error('Failed to toggle favorite status:', err);
    }
  }

  async function handleSave() {
    const { title, text, tags, icon, summary, externalUrl } = draft.values;
    const isLink = Boolean(article?.external_url);

    const missing = missingTitleMessage(title, t);
    draft.setTitleError(missing ?? '');
    if (missing) {
      document.getElementById(ARTICLE_TITLE_ID)?.focus();
      return;
    }

    if (isLink && !EXTERNAL_URL.test(externalUrl)) {
      draft.setError(t('articleLink.invalid'));
      return;
    }

    const editedPlugin = pluginArticle(article.text ?? '', plugins ?? [])?.plugin;
    if (editedPlugin && !pluginArticle(text, [editedPlugin])) {
      draft.setError(t('plugins.empty'));
      return;
    }


    try {
      setIsSaving(true);
      draft.setError('');

      // A link item has no text editor; its text stays as it is.
      const finalText = isLink
        ? text
        : await draft.uploadPending(text, article?.is_news ? 'news' : 'article');

      // Uploaded now: the draft keeps the stored URLs if the save below fails.
      if (finalText !== text) draft.set('text', finalText);

      const updated = await updateArticle(id, {
        title,
        text: finalText,
        tags,
        icon,
        summary,
        ...(isLink ? { external_url: externalUrl } : {}),
        // Die Abteilung wird nur übertragen, wenn tatsächlich eine andere gewählt wurde.
        ...(editDepartmentId === null ||
          editDepartmentId === article?.department?.department_id
          ? {}
          : { department_id: editDepartmentId }),
      });

      setArticle(updated);
      setIsEditing(false);
      window.scrollTo(0, 0);

      // Saving keeps the old text as a version and gives the article a new
      // id; the old address now answers 404, so the page moves to the new one.
      if (updated?.article_id && String(updated.article_id) !== String(id)) {
        navigate(`/article/${updated.article_id}`, { replace: true });
      }
    } catch (err) {
      console.error('Failed to save article:', err);

      // Someone saved the article meanwhile: nothing was saved, the edits are
      // still in the editor to copy over into the current version.
      if (err?.status === 409 && err?.data?.code === 'ARTICLE_SUPERSEDED') {
        const currentId = err.data.currentArticleId;
        draft.setError(
          <>
            {t('articlePage.superseded')}{' '}
            {currentId && (
              <Link to={`/article/${currentId}`} target="_blank" rel="noopener noreferrer">
                {t('articlePage.openCurrentVersion')}
              </Link>
            )}
          </>,
        );
        return;
      }

      const titleProblem = titleErrorMessage(err, title, t);
      if (titleProblem) {
        draft.setTitleError(titleProblem);
        document.getElementById(ARTICLE_TITLE_ID)?.focus();
      } else {
        draft.setError(err?.message || t('articlePage.saveErrorFallback'));
      }
    } finally {
      setIsSaving(false);
    }
  }

  if (loading) {
    return (
      <PageLayout showSearch showAiSearch>
        <PageMain className="article-page">
          <p>{t('articlePage.loading')}</p>
        </PageMain>
      </PageLayout>
    );
  }

  if (error || !article) {
    return (
      <NotFound
        eyebrow={t('notFound.articleEyebrow')}
        title={t('notFound.articleTitle')}
        description={t('notFound.articleDescription')}
      />
    );
  }

  // An article that is one plugin block (a process) is shown, and edited, as
  // wide as the page that created it.
  const articlePlugin = pluginArticle(article.text ?? '', plugins ?? [])?.plugin ?? null;

  return (
    <PageLayout showSearch showAiSearch sidebarClosed={Boolean(articlePlugin)}>
      <PageMain className={`article-page${articlePlugin ? ' page-main--wide' : ''}`}>
        <ArticleView
          article={article}
          isEditing={isEditing}
          onStartEditing={startEditing}
          isFavorite={isFavorite}
          onToggleFavorite={toggleFavorite}
          onArchive={onArchive}
        >
          {isEditing && plugins === null ? (
            // Which editor an article gets depends on the plugins; wait for them.
            <p>{t('articlePage.loading')}</p>
          ) : isEditing ? (
            <ArticleEditorForm
              draft={draft}
              linkMode={Boolean(article.external_url)}
              // An article that is one plugin block is edited in the plugin's editor.
              plugin={articlePlugin}
              pluginScope={article.department?.department_id ?? null}
              canReimport={canReimport}
              saving={isSaving}
              onSubmit={handleSave}
              onCancel={cancelEditing}
              labels={{
                title: t('articlePage.editTitleLabel'),
                cancel: t('articlePage.cancelButtonLabel'),
                submit: t('articlePage.saveButtonLabel'),
                saving: t('articlePage.savingLabel'),
              }}
              uploadTarget={article.is_news ? 'news' : 'article'}
              departments={authoringDepartments}
              departmentId={editDepartmentId}
              onDepartmentChange={setEditDepartmentId}
              sidebar={{
                article,
                currentDepartmentName: article.department?.name,
              }}
            />
          ) : (
            <>
              {article?.external_url ? (
                <ExternalLinkNotice
                  url={article.external_url}
                  canEdit={article.canEdit}
                />
              ) : (
                <ArticleBody
                  articleText={articleText}
                  headings={headings}
                  navigation={article?.navigation}
                  departmentId={article?.department?.department_id ?? null}
                />
              )}

              <ArticleSidebar
                headings={headings}
                activeHeadingId={activeHeadingId}
              />
            </>
          )}
        </ArticleView>
      </PageMain>
    </PageLayout>
  );
}

export default ArticlePage;